import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronsUpDown, Pencil, Trash2 } from 'lucide-react'
import inviteeStyles from '@/features/invitee/styles/invitesParity.module.css'
import { showApiError, showSuccess } from '@/lib/utils/notifications'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { DatePicker } from '@/components/ui/DatePicker'
import {
  ListingDataTableCard,
  ListingDataTableScroll,
  LISTING_DATA_TABLE_BODY_CLASS,
  LISTING_DATA_TABLE_CLASS,
  LISTING_DATA_TABLE_HEAD_CLASS,
} from '@/components/ui/listingDataTable'
import companyFormStyles from '@/features/company/styles/companyAdd.module.css'
import { primaryEntityActionButtonClass, secondaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import { cn } from '@/lib/utils/helpers'
import { ChangeReasonModal } from '../components/ChangeReasonModal'
import {
  deletePolicyDeclaration,
  getPolicyDeclarations,
  savePolicyDeclaration,
} from '../services/policyDeclarationService'
import type {
  PolicyDeclarationItem,
  PolicyDeclarationListParams,
} from '../types/policyDeclaration'
import {
  isEndDateBeforeStart,
  toDatePickerMinDate,
} from '../utils/effectiveDateRange'
import { END_DATE_BEFORE_START_MESSAGE } from '../constants/effectiveDateRange'
import { ListingTableLoadingRow } from '@/components/ui/ModuleScreenLoading'

type EditableRow = PolicyDeclarationItem & { client_id: string }
type RowField = 'policy_id' | 'template_name' | 'policy_start_date' | 'policy_end_date'
type RowErrors = Partial<Record<RowField, string>>

const DEFAULT_PARAMS: Required<PolicyDeclarationListParams> = {
  page: 1,
  perPage: 15,
  sortBy: 'asc',
  orderBy: '',
}

function makeClientId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

function normalizeDate(value: unknown): string {
  if (value == null || value === '') return ''
  const valueStr = String(value).trim()
  if (!valueStr) return ''
  const isoPrefix = valueStr.match(/^(\d{4}-\d{2}-\d{2})/)
  if (isoPrefix) return isoPrefix[1]

  const slash = valueStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (slash) {
    const mm = slash[1].padStart(2, '0')
    const dd = slash[2].padStart(2, '0')
    return `${slash[3]}-${mm}-${dd}`
  }

  const dash = valueStr.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/)
  if (dash) {
    const mm = dash[1].padStart(2, '0')
    const dd = dash[2].padStart(2, '0')
    return `${dash[3]}-${mm}-${dd}`
  }

  return ''
}

function endOfYear(isoDate: string): string {
  const match = isoDate.match(/^(\d{4})-\d{2}-\d{2}$/)
  return match ? `${match[1]}-12-31` : ''
}

function normalizeTextField(value: unknown): string {
  if (value == null || value === '') return ''
  return String(value)
    .trim()
    .replace(/^"+|"+$/g, '')
    .replace(/,+$/g, '')
}

function toEditableRow(item: PolicyDeclarationItem): EditableRow {
  return {
    ...item,
    client_id: makeClientId(),
    policy_id: normalizeTextField(item.policy_id),
    template_name: normalizeTextField(item.template_name),
    policy_start_date: normalizeDate(item.policy_start_date),
    policy_end_date: normalizeDate(item.policy_end_date),
  }
}

function validateRow(row: EditableRow): RowErrors {
  const errors: RowErrors = {}
  if (!String(row.template_name ?? '').trim()) errors.template_name = 'Filename is required.'
  if (!row.policy_start_date) errors.policy_start_date = 'Start Date is required.'
  if (!row.policy_end_date) errors.policy_end_date = 'End Date is required.'
  if (isEndDateBeforeStart(row.policy_start_date, row.policy_end_date)) {
    errors.policy_end_date = END_DATE_BEFORE_START_MESSAGE
  }
  return errors
}

function createSnapshot(row: EditableRow): Pick<
  EditableRow,
  'policy_id' | 'template_name' | 'policy_start_date' | 'policy_end_date'
> {
  return {
    policy_id: row.policy_id,
    template_name: row.template_name,
    policy_start_date: row.policy_start_date,
    policy_end_date: row.policy_end_date,
  }
}

export function TermsConditionsListPage() {
  const navigate = useNavigate()
  const [rows, setRows] = useState<EditableRow[]>([])
  const [loading, setLoading] = useState(false)
  const [params, setParams] = useState<Required<PolicyDeclarationListParams>>(DEFAULT_PARAMS)
  const [pagination, setPagination] = useState({ current: 1, last: 1 })
  const [submittingRowId, setSubmittingRowId] = useState<string | null>(null)
  const [errorsByRow, setErrorsByRow] = useState<Record<string, RowErrors>>({})
  const [deleteTarget, setDeleteTarget] = useState<EditableRow | null>(null)
  const [reasonOpen, setReasonOpen] = useState(false)
  const pendingSaveRef = useRef<EditableRow | null>(null)
  const baselineByPdIdRef = useRef<Record<number, ReturnType<typeof createSnapshot>>>({})
  const mountedRef = useRef(false)

  const load = useCallback(async (nextParams: Required<PolicyDeclarationListParams>) => {
    setLoading(true)
    try {
      const response = await getPolicyDeclarations(nextParams)
      const editableRows = response.items.map(toEditableRow)
      setRows(editableRows)
      setPagination({ current: response.current, last: response.last })
      const nextBaseline: Record<number, ReturnType<typeof createSnapshot>> = {}
      editableRows.forEach((row) => {
        if (row.pd_id) nextBaseline[row.pd_id] = createSnapshot(row)
      })
      baselineByPdIdRef.current = nextBaseline
      setErrorsByRow({})
    } catch (error) {
      showApiError(error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (mountedRef.current) return
    mountedRef.current = true
    void load(DEFAULT_PARAMS)
  }, [load])

  const isDirtyExistingRow = (row: EditableRow): boolean => {
    if (!row.pd_id) return false
    const baseline = baselineByPdIdRef.current[row.pd_id]
    if (!baseline) return false
    const current = createSnapshot(row)
    return (
      baseline.policy_id !== current.policy_id ||
      baseline.template_name !== current.template_name ||
      baseline.policy_start_date !== current.policy_start_date ||
      baseline.policy_end_date !== current.policy_end_date
    )
  }

  const updateRow = (clientId: string, patch: Partial<EditableRow>) => {
    setRows((current) =>
      current.map((row) => (row.client_id === clientId ? { ...row, ...patch } : row)),
    )
    setErrorsByRow((current) => {
      if (!current[clientId]) return current
      const next = { ...current }
      delete next[clientId]
      return next
    })
  }

  const persistRow = async (row: EditableRow, reason?: string) => {
    setSubmittingRowId(row.client_id)
    try {
      await savePolicyDeclaration(
        {
          policy_id: normalizeTextField(row.policy_id),
          template_name: normalizeTextField(row.template_name),
          policy_start_date: row.policy_start_date,
          policy_end_date: row.policy_end_date,
          ...(reason ? { change_reason: reason } : {}),
        },
        row.pd_id,
      )
      showSuccess('Terms & Conditions Added Successfully!')
      await load(params)
    } catch (error) {
      showApiError(error)
    } finally {
      setSubmittingRowId(null)
    }
  }

  const handleSave = async (row: EditableRow) => {
    const rowErrors = validateRow(row)
    if (Object.keys(rowErrors).length > 0) {
      setErrorsByRow((current) => ({ ...current, [row.client_id]: rowErrors }))
      return
    }

    if (row.pd_id && isDirtyExistingRow(row)) {
      pendingSaveRef.current = row
      setReasonOpen(true)
      return
    }

    await persistRow(row)
  }

  const handleReasonSubmit = async (reason: string) => {
    const pending = pendingSaveRef.current
    if (!pending) return
    await persistRow(pending, reason)
    setReasonOpen(false)
    pendingSaveRef.current = null
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    if (!deleteTarget.pd_id) {
      setRows((current) => current.filter((row) => row.client_id !== deleteTarget.client_id))
      setDeleteTarget(null)
      return
    }

    try {
      await deletePolicyDeclaration(deleteTarget.pd_id)
      showSuccess('Terms & Conditions Deleted Successfully!')
      setRows((current) => current.filter((row) => row.client_id !== deleteTarget.client_id))
      setDeleteTarget(null)
    } catch (error) {
      showApiError(error)
    }
  }

  const handleAddRow = () => {
    setRows((current) => [
      {
        client_id: makeClientId(),
        policy_id: '',
        template_name: '',
        policy_start_date: '',
        policy_end_date: '',
        policy_status: '0',
      },
      ...current,
    ])
  }

  const toggleSort = (orderBy: 'policy_id' | 'template_name') => {
    const nextSortBy: 'asc' | 'desc' =
      params.orderBy === orderBy && params.sortBy === 'asc' ? 'desc' : 'asc'
    const nextParams = { ...params, orderBy, sortBy: nextSortBy, page: 1 }
    setParams(nextParams)
    void load(nextParams)
  }

  const sortIndicator = useMemo(
    () => (column: 'policy_id' | 'template_name') => {
      const isActive = params.orderBy === column
      return (
        <span
          aria-hidden="true"
          className={isActive ? inviteeStyles.sortIconActive : inviteeStyles.sortIcon}
        >
          {isActive
            ? params.sortBy === 'asc'
              ? '▲'
              : '▼'
            : <ChevronsUpDown className="h-3 w-3" />}
        </span>
      )
    },
    [params.orderBy, params.sortBy],
  )

  const goToPage = (pageNo: number) => {
    const nextParams = { ...params, page: pageNo }
    setParams(nextParams)
    void load(nextParams)
  }

  const openReactEdit = (id: number | undefined) => {
    if (!id) return
    navigate(`/terms-and-conditions/edit/${id}`)
  }

  const fieldClassName = cn(companyFormStyles.customFormControl, 'min-w-[180px]')

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        <button
          type="button"
          onClick={handleAddRow}
          className={cn(primaryEntityActionButtonClass, 'px-4 py-2 text-sm font-medium')}
        >
          Add
        </button>
      </div>

      <ListingDataTableCard>
        <ListingDataTableScroll>
          <table className={LISTING_DATA_TABLE_CLASS}>
            <thead className={LISTING_DATA_TABLE_HEAD_CLASS}>
              <tr>
                <th
                  className="cursor-pointer px-4 py-3 text-left select-none"
                  onClick={() => toggleSort('policy_id')}
                >
                  <button
                    type="button"
                    className={inviteeStyles.sortHeaderButton}
                    onClick={(e) => { e.stopPropagation(); toggleSort('policy_id') }}
                  >
                    <span className={inviteeStyles.sortHeaderLabel}>Document ID</span>
                    {sortIndicator('policy_id')}
                  </button>
                </th>
                <th
                  className="cursor-pointer px-4 py-3 text-left select-none"
                  onClick={() => toggleSort('template_name')}
                >
                  <button
                    type="button"
                    className={inviteeStyles.sortHeaderButton}
                    onClick={(e) => { e.stopPropagation(); toggleSort('template_name') }}
                  >
                    <span className={inviteeStyles.sortHeaderLabel}>Filename</span>
                    {sortIndicator('template_name')}
                  </button>
                </th>
                <th className="px-4 py-3 text-left">Effective Dates</th>
                <th className="px-4 py-3 text-left">Action</th>
              </tr>
            </thead>
          <tbody className={LISTING_DATA_TABLE_BODY_CLASS}>
            {loading ? (
              <ListingTableLoadingRow colSpan={4} />
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-slate-500">
                  No terms & conditions found.
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const rowErrors = errorsByRow[row.client_id] ?? {}
                return (
                  <tr key={row.client_id} className="align-top">
                    <td className="px-4 py-3">
                      <input
                        type="text"
                        value={row.policy_id}
                        onChange={(event) =>
                          updateRow(row.client_id, { policy_id: event.target.value })
                        }
                        placeholder="Document ID"
                        className={fieldClassName}
                      />
                      <div className="mt-1 flex items-center gap-2">
                        {row.policy_status === '1' ? (
                          <span className="text-[11px] font-medium text-slate-600">(active)</span>
                        ) : null}
                        {rowErrors.policy_id ? (
                          <span className="text-xs text-red-600">{rowErrors.policy_id}</span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="text"
                        value={row.template_name}
                        onChange={(event) =>
                          updateRow(row.client_id, { template_name: event.target.value })
                        }
                        placeholder="Filename"
                        className={fieldClassName}
                      />
                      {rowErrors.template_name ? (
                        <p className="mt-1 text-xs text-red-600">{rowErrors.template_name}</p>
                      ) : null}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-start gap-2">
                        <div className="min-w-[180px]">
                          <DatePicker
                            value={row.policy_start_date}
                            onChange={(event) => {
                              const nextStart = event
                              updateRow(row.client_id, {
                                policy_start_date: nextStart,
                                ...(nextStart ? { policy_end_date: endOfYear(nextStart) } : {}),
                              })
                            }}
                            placeholder="MM/DD/YYYY"
                            allowFutureDates={true}
                            showHelperText={false}
                            className={fieldClassName}
                          />
                          {rowErrors.policy_start_date ? (
                            <p className="mt-1 text-xs text-red-600">{rowErrors.policy_start_date}</p>
                          ) : null}
                        </div>
                        <span className="pt-2 text-slate-600">To</span>
                        <div className="min-w-[180px]">
                          <DatePicker
                            value={row.policy_end_date}
                            onChange={(value) =>
                              updateRow(row.client_id, { policy_end_date: value })
                            }
                            placeholder="MM/DD/YYYY"
                            allowFutureDates={true}
                            showHelperText={false}
                            className={fieldClassName}
                            minDate={toDatePickerMinDate(row.policy_start_date)}
                          />
                          {rowErrors.policy_end_date ? (
                            <p className="mt-1 text-xs text-red-600">{rowErrors.policy_end_date}</p>
                          ) : null}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => handleSave(row)}
                          disabled={submittingRowId === row.client_id}
                          className={cn(primaryEntityActionButtonClass, 'px-2 py-1 text-xs')}
                        >
                          {submittingRowId === row.client_id ? 'Saving...' : 'Save'}
                        </button>
                        {row.pd_id ? (
                          <button
                            type="button"
                            onClick={() => openReactEdit(row.pd_id)}
                            className={cn(secondaryEntityActionButtonClass, 'px-2 py-1 text-xs text-[#1597ff] hover:text-[#0d7acc]')}
                            title="Edit"
                            aria-label="Edit"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        ) : null}
                        {row.policy_status !== '1' ? (
                          <button
                            type="button"
                            onClick={() => setDeleteTarget(row)}
                            className={cn(secondaryEntityActionButtonClass, 'px-2 py-1 text-xs text-red-600 hover:text-red-700')}
                            title="Delete"
                            aria-label="Delete"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </ListingDataTableScroll>
      </ListingDataTableCard>

      {pagination.last > 1 ? (
        <div className="flex items-center justify-between text-sm text-slate-600">
          <span>
            Page {pagination.current} of {pagination.last}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => goToPage(Math.max(1, pagination.current - 1))}
              disabled={pagination.current <= 1}
              className="rounded border border-slate-300 px-3 py-1.5 hover:bg-slate-100 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => goToPage(Math.min(pagination.last, pagination.current + 1))}
              disabled={pagination.current >= pagination.last}
              className="rounded border border-slate-300 px-3 py-1.5 hover:bg-slate-100 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      ) : null}

      <ChangeReasonModal
        isOpen={reasonOpen}
        submitting={submittingRowId !== null}
        onClose={() => {
          setReasonOpen(false)
          pendingSaveRef.current = null
        }}
        onSubmit={handleReasonSubmit}
      />

      <ConfirmDialog
        isOpen={deleteTarget !== null}
        title="Delete Terms & Conditions"
        message="Do You Want To Delete this Terms & Conditions ?"
        primaryLabel="Delete"
        onPrimary={() => void handleDelete()}
        secondaryLabel="Cancel"
        onSecondary={() => setDeleteTarget(null)}
        variant="warning"
      />
    </div>
  )
}
